import { useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Notice,
  Status,
  type Tone,
} from '@gatecheckpro/design-system';
import type { Venue } from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';
import { StageOpsLinksPanel } from './StageOpsLinksPanel.tsx';

/**
 * Who works this event, and where they may go.
 *
 * Deliberately not a ticket screen, and worded so nobody could mistake it for
 * one: no price, no order, no purchase. A crew pass is operational access, and
 * the moment it starts borrowing commerce language somebody will eventually
 * make it borrow commerce behaviour too.
 *
 * The page's one job beyond issuing is to make the access legible *before* it
 * is granted. An organizer choosing "vendor" should not have to know what the
 * system thinks a vendor may do — the zones are chosen explicitly and shown
 * back in plain words, because a badge nobody read carefully is how somebody
 * ends up backstage.
 */

interface OperationalCredential {
  readonly id: string;
  readonly credentialId: string;
  readonly credentialType: 'CREW' | 'VENDOR' | 'STAFF';
  readonly status: 'ACTIVE' | 'SUPERSEDED' | 'REVOKED';
  readonly version: number;
  readonly validFrom: string;
  readonly validUntil: string;
  readonly revokedReason: string | null;
  readonly subject: { id: string; displayName: string; reference: string | null };
  readonly zones: ReadonlyArray<{ zoneId: string; code: string; displayName: string }>;
}

const TYPE_LABELS: Record<OperationalCredential['credentialType'], string> = {
  CREW: 'Crew',
  VENDOR: 'Vendor',
  STAFF: 'Staff',
};

/**
 * What each state means to the person reading it.
 *
 * "Replaced" rather than "superseded": the organizer replaced a badge, and the
 * word they used is the word they should see back.
 */
const STATE_LABELS: Record<OperationalCredential['status'], { label: string; tone: Tone }> = {
  ACTIVE: { label: 'Active', tone: 'positive' },
  SUPERSEDED: { label: 'Replaced', tone: 'neutral' },
  REVOKED: { label: 'Revoked', tone: 'critical' },
};

/**
 * What the organizer is about to grant, in words.
 *
 * Extracted and pure so it can be tested without a router or a server: the
 * wording is the safety property here, not the plumbing. Access is easy to
 * grant by accident and hard to notice afterwards, so this reads the zones back
 * by name and says plainly what the pass does *not* open.
 */
export function OperationalAccessReview(props: {
  readonly displayName: string;
  readonly credentialType: OperationalCredential['credentialType'];
  readonly zones: ReadonlyArray<{ displayName: string }>;
}) {
  return (
    <div className="gc-stack" data-testid="operational-access-review">
      <p>
        <strong>{props.displayName}</strong> will hold a{' '}
        {TYPE_LABELS[props.credentialType].toLowerCase()} pass.
      </p>
      <p data-testid="operational-access-review-zones">
        It opens: {props.zones.map((zone) => zone.displayName).join(', ')}.
      </p>
      <p className="gc-muted">
        It opens nothing else. Anywhere not listed here will refuse this pass.
      </p>
    </div>
  );
}

export function EventOperationalAccessPage() {
  const { event, organizationId } = useEventWorkspace();
  const command = useCommand<OperationalCredential>();
  const base = `/api/v1/organizer/organizations/${organizationId}`;
  const scoped = `/api/v1/organizer/events/${event.eventId}/operational-credentials?organizationId=${organizationId}`;

  const { resource: credentials, reload } = useResource<{ credentials: OperationalCredential[] }>(
    scoped,
  );
  const { resource: venue } = useResource<Venue>(
    event.venue === null ? null : `${base}/venues/${event.venue.venueId}`,
  );

  const [issuing, setIssuing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [reference, setReference] = useState('');
  const [credentialType, setCredentialType] =
    useState<OperationalCredential['credentialType']>('CREW');
  const [zoneIds, setZoneIds] = useState<string[]>([]);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [editingCredentialId, setEditingCredentialId] = useState<string | null>(null);
  const [editZoneIds, setEditZoneIds] = useState<string[]>([]);

  const zones = venue.status === 'loaded' ? venue.data.zones : [];
  const chosenZones = zones.filter((zone) => zoneIds.includes(zone.accessZoneId));

  async function issue(): Promise<void> {
    const result = await command.run(scoped, {
      method: 'POST',
      body: {
        subject: {
          kind: 'NEW',
          displayName: displayName.trim(),
          reference: reference.trim() === '' ? null : reference.trim(),
        },
        credentialType,
        accessZoneIds: zoneIds,
        validFrom: new Date(validFrom).toISOString(),
        validUntil: new Date(validUntil).toISOString(),
      },
    });
    if (result !== null) {
      setIssuing(false);
      setConfirming(false);
      setDisplayName('');
      setReference('');
      setZoneIds([]);
      reload();
    }
  }

  /**
   * Changes what a pass opens, without replacing it.
   *
   * Deliberately its own action next to "Stop this pass". They are different
   * things to the person holding the badge: editing zones leaves the pass in
   * their pocket working for the doors they still have, stopping it means they
   * need a new one. Offering only the second made every zone change a
   * replacement.
   */
  function editZones(credential: OperationalCredential): void {
    setEditingCredentialId(credential.id);
    setEditZoneIds(credential.zones.map((zone) => zone.zoneId));
  }

  async function saveZones(): Promise<void> {
    if (editingCredentialId === null || editZoneIds.length === 0) return;
    const result = await command.run(
      `/api/v1/organizer/events/${event.eventId}/operational-credentials/` +
        `${editingCredentialId}/entitlements?organizationId=${organizationId}`,
      { method: 'PUT', body: { accessZoneIds: editZoneIds } },
    );
    if (result !== null) {
      setEditingCredentialId(null);
      reload();
    }
  }

  async function revoke(credential: OperationalCredential): Promise<void> {
    const reason = window.prompt(`Why is ${credential.subject.displayName}’s pass being stopped?`);
    if (reason === null || reason.trim() === '') return;
    const result = await command.run(
      `/api/v1/organizer/events/${event.eventId}/operational-credentials/${credential.id}/revoke` +
        `?organizationId=${organizationId}`,
      { method: 'POST', body: { reason: reason.trim() } },
    );
    if (result !== null) reload();
  }

  if (credentials.status === 'loading') return <p>Loading operational access…</p>;
  if (credentials.status === 'failed') return <FailureNotice error={credentials.error} />;

  const live = credentials.data.credentials.filter((entry) => entry.status === 'ACTIVE');
  const past = credentials.data.credentials.filter((entry) => entry.status !== 'ACTIVE');

  return (
    <section className="gc-stack" data-testid="operational-access">
      <Card title="Operational access" eyebrow="Staff, crew and vendors">
        <p>
          Passes for the people who work this event. These are not tickets: nothing here is sold,
          and none of it touches capacity or availability.
        </p>
        {event.venue === null ? (
          <Notice tone="caution" title="No venue yet">
            This event has no venue yet, so there are no zones to grant access to.
          </Notice>
        ) : (
          <Button onClick={() => setIssuing(true)} disabled={issuing}>
            Issue a pass
          </Button>
        )}
      </Card>

      {issuing && (
        <Card title="Issue an operational pass">
          {!confirming ? (
            <form
              className="gc-stack"
              onSubmit={(formEvent) => {
                formEvent.preventDefault();
                setConfirming(true);
              }}
            >
              <Field
                label="Who is this for?"
                hint="The name gate staff will see. Nothing else about them is shown at the gate."
                value={displayName}
                onChange={(changed) => setDisplayName(changed.target.value)}
                required
                maxLength={120}
              />
              <Field
                label="Your reference (optional)"
                hint="e.g. CREW-014"
                value={reference}
                onChange={(changed) => setReference(changed.target.value)}
                maxLength={80}
              />

              <label className="gc-field">
                <span>Pass type</span>
                <select
                  value={credentialType}
                  onChange={(changed) =>
                    setCredentialType(
                      changed.target.value as OperationalCredential['credentialType'],
                    )
                  }
                >
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              {/*
                Said plainly, because the opposite assumption is the intuitive
                one. The type is a label for the person reading the screen at
                the gate; it opens nothing on its own.
              */}
              <p className="gc-muted">
                The pass type is a label. It grants no access by itself — choose the zones below.
              </p>

              <fieldset>
                <legend>Which zones may this pass open?</legend>
                {zones.length === 0 ? (
                  <EmptyState title="This venue has no zones yet">
                    <p>Add zones to the venue before issuing operational passes.</p>
                  </EmptyState>
                ) : (
                  zones.map((zone) => (
                    <label key={zone.accessZoneId} className="gc-checkbox">
                      <input
                        type="checkbox"
                        checked={zoneIds.includes(zone.accessZoneId)}
                        onChange={(changed) =>
                          setZoneIds((current) =>
                            changed.target.checked
                              ? [...current, zone.accessZoneId]
                              : current.filter((id) => id !== zone.accessZoneId),
                          )
                        }
                      />
                      {zone.displayName}
                    </label>
                  ))
                )}
              </fieldset>

              <Field
                label="Valid from"
                type="datetime-local"
                value={validFrom}
                onChange={(changed) => setValidFrom(changed.target.value)}
                required
              />
              <Field
                label="Valid until"
                hint="A pass that works forever is a pass nobody remembers to take back."
                type="datetime-local"
                value={validUntil}
                onChange={(changed) => setValidUntil(changed.target.value)}
                required
              />

              <div className="gc-actions">
                <Button type="submit" disabled={zoneIds.length === 0 || displayName.trim() === ''}>
                  Review
                </Button>
                <Button type="button" variant="quiet" onClick={() => setIssuing(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            /*
              The review step exists because access is easy to grant by accident
              and hard to notice afterwards. Read back in words, not identifiers.
            */
            <div className="gc-stack">
              <OperationalAccessReview
                displayName={displayName.trim()}
                credentialType={credentialType}
                zones={chosenZones}
              />
              {command.error !== null && <FailureNotice error={command.error} />}
              <div className="gc-actions">
                <Button onClick={() => void issue()} busy={command.busy}>
                  Issue pass
                </Button>
                <Button type="button" variant="quiet" onClick={() => setConfirming(false)}>
                  Back
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card title="Active passes">
        {live.length === 0 ? (
          <EmptyState title="Nobody has operational access yet">
            <p>Issue a pass to let crew, vendors or staff through the zones you choose.</p>
          </EmptyState>
        ) : (
          <ul className="gc-list" data-testid="operational-access-active">
            {live.map((credential) => (
              <li key={credential.id}>
                <div>
                  <strong>{credential.subject.displayName}</strong>{' '}
                  <Status tone="info" label={TYPE_LABELS[credential.credentialType]} />
                </div>
                <p>Opens: {credential.zones.map((zone) => zone.displayName).join(', ')}</p>
                <p className="gc-muted">
                  {new Date(credential.validFrom).toLocaleString()} –{' '}
                  {new Date(credential.validUntil).toLocaleString()}
                </p>
                {editingCredentialId === credential.id ? (
                  <div className="gc-stack gc-stack--tight">
                    <fieldset className="gc-fieldset">
                      <legend className="gc-field__label">Zones this pass opens</legend>
                      {zones.map((zone) => (
                        <label className="gc-checkbox" key={zone.accessZoneId}>
                          <input
                            type="checkbox"
                            checked={editZoneIds.includes(zone.accessZoneId)}
                            onChange={(input) =>
                              setEditZoneIds((current) =>
                                input.target.checked
                                  ? [...current, zone.accessZoneId]
                                  : current.filter((id) => id !== zone.accessZoneId),
                              )
                            }
                          />
                          <span className="gc-checkbox__body">
                            <span className="gc-checkbox__title">{zone.displayName}</span>
                          </span>
                        </label>
                      ))}
                    </fieldset>
                    <p className="gc-muted">
                      The pass itself does not change. {credential.subject.displayName} keeps the
                      one they already have, and it stops opening the zones you remove as soon as
                      the venue receives the update.
                    </p>
                    <div className="gc-row">
                      <Button onClick={() => void saveZones()} disabled={editZoneIds.length === 0}>
                        Save zones
                      </Button>
                      <Button variant="quiet" onClick={() => setEditingCredentialId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="gc-row">
                    <Button variant="quiet" onClick={() => editZones(credential)}>
                      Change zones
                    </Button>
                    <Button variant="quiet" onClick={() => void revoke(credential)}>
                      Stop this pass
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {past.length > 0 && (
        <Card title="Replaced and stopped" eyebrow="Kept for incident review">
          <ul className="gc-list" data-testid="operational-access-history">
            {past.map((credential) => (
              <li key={credential.id}>
                <strong>{credential.subject.displayName}</strong>{' '}
                <Status
                  tone={STATE_LABELS[credential.status].tone}
                  label={STATE_LABELS[credential.status].label}
                />
                {credential.revokedReason !== null && (
                  <p className="gc-muted">{credential.revokedReason}</p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <StageOpsLinksPanel eventId={event.eventId} organizationId={organizationId} />
    </section>
  );
}
